import React, { useCallback } from 'react';
import { Edge } from 'reactflow';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../../util/autosave/nodedata_autosave';
import { FlowService } from '../../../service/FlowService';

export function useThirdPartyEdges(
  edges: Edge[],
  id: string,
  flowId: string | undefined,
  setEdges: (value: Edge[] | ((prev: Edge[]) => Edge[])) => void,
  setNodeState: (value: unknown) => void,
  setSaving: (saving: { lastSaved?: string; status: string; finished?: boolean; flowId?: string }) => void,
  setSchema: (schema: Record<string, { type: string; required: boolean }>) => void,
) {
  const setNotification = useNotificationStore(
    state => state.setNotification,
  );

  const removeConnectedEdges = useCallback(async () => {
    const connectedEdges = edges.filter(
      (e: Edge) =>
        e.target === id && (e.targetHandle as string)?.startsWith('field_'),
    );
    if (connectedEdges.length === 0 || !flowId) return;
    try {
      for (const edge of connectedEdges) {
        await FlowService.deleteEdge({ flowId, edgeId: edge.id });
      }
      const remainingEdges = edges.filter(
        (e: Edge) => !connectedEdges.some(c => c.id === e.id),
      );
      setEdges(remainingEdges);
      nodeDataAutoSaveDynamic({
        newEdges: remainingEdges,
        setNodeState,
        id,
        flowId,
        setSaving,
        changeType: 'delete-edges',
        deleteEdges: connectedEdges,
        setSchema,
      });
    } catch (err: unknown) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(err),
      });
    }
  }, [
    edges,
    id,
    flowId,
    setEdges,
    setNodeState,
    setSaving,
    setSchema,
    setNotification,
  ]);

  const removeEdgesForField = useCallback(
    async (fieldName: string) => {
      const handleId = `field_${fieldName}`;
      const connectedEdges = edges.filter(
        (e: Edge) =>
          e.target === id && (e.targetHandle as string) === handleId,
      );
      if (connectedEdges.length === 0 || !flowId) return;
      try {
        for (const edge of connectedEdges) {
          await FlowService.deleteEdge({ flowId, edgeId: edge.id });
        }
        const remainingEdges = edges.filter(
          (e: Edge) => !connectedEdges.some(c => c.id === e.id),
        );
        setEdges(remainingEdges);
        nodeDataAutoSaveDynamic({
          newEdges: remainingEdges,
          setNodeState,
          id,
          flowId,
          setSaving,
          changeType: 'delete-edges',
          deleteEdges: connectedEdges,
          setSchema,
        });
      } catch (err: unknown) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(err),
        });
      }
    },
    [
      edges,
      id,
      flowId,
      setEdges,
      setNodeState,
      setSaving,
      setSchema,
      setNotification,
    ],
  );

  return { removeConnectedEdges, removeEdgesForField };
}
